import { useMutation, useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router'

import { requestOtp, verifyOtp, type PatientSession } from '../api/patientAuth'
import {
  fetchProcedure,
  fetchProcedures,
  procedureHeading,
  procedureKey,
  proceduresKey,
  submitProcedureRequest,
  type ProcedureDetail,
  type ProcedureListItem,
} from '../api/procedures'
import { Icon } from '../components/Icon'
import { PriceCaveat } from '../components/PriceCaveat'
import { SearchReading } from '../components/search/SearchReading'
import { searchDestination } from '../components/search/destination'
import {
  PAGE_GUTTER_DESKTOP,
  PageBand,
  PageShell,
} from '../components/PageShell'
import { SearchInput } from '../components/SearchInput'
import { ApiError } from '../lib/api'
import { readPrice } from '../lib/money'

/**
 * Diagnostics & procedures (M8-T03; popular-first + lead capture M12-T04) at
 * `/diagnostics`. The default view leads with the most-searched procedures
 * ("Common" badge, driven by the backend `popular` flag — no hard-coded slugs);
 * "Search all procedures" reveals the 15-category browse, and a `?q=` deep link
 * (from the home suggestions) searches the full catalog. "My procedure is not
 * listed" captures a free-text AMTC concierge lead (ADR-0015 — a lead, not a
 * booking). Selecting a procedure lists the partner hospitals that offer it;
 * tapping one opens the M11 service-request form.
 */
export function DiagnosticsSearch(): ReactNode {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // Two taxonomies, one selection: picking a facet sets whichever of these it
  // belongs to and clears the other, so the chip row always has exactly one
  // active entry and the backend is never sent a contradictory pair.
  const [category, setCategory] = useState<string | null>(null)
  const [specialty, setSpecialty] = useState<string | null>(null)
  const selectFacet = (facet: Facet | null) => {
    setCategory(facet?.kind === 'category' ? facet.key : null)
    setSpecialty(facet?.kind === 'specialty' ? facet.key : null)
  }
  const [q, setQ] = useState(() => searchParams.get('q') ?? '')
  const [selected, setSelected] = useState<string | null>(null)
  const [browseAll, setBrowseAll] = useState(false)
  const [notListed, setNotListed] = useState(false)

  const back = () => {
    if (notListed) setNotListed(false)
    else if (selected) setSelected(null)
    else navigate(-1)
  }

  return (
    <PageShell measure={640}>
      <header
        className={`flex items-center gap-1.5 border-b border-line-1 px-3 py-2 ${PAGE_GUTTER_DESKTOP}`}
      >
        <button
          type="button"
          aria-label="Back"
          onClick={back}
          className="rounded p-1.5 text-ink-2 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <Icon name="chevl" size={18} />
        </button>
        <span className="text-[13px] font-medium text-ink-1">
          Find a test or procedure
        </span>
      </header>
      {/* One band for the body: everything nested inherits the 640px measure
          through context and no padding value moves, so mobile is unchanged
          by construction. */}
      <PageBand
        className="flex flex-1 flex-col"
        contentClassName="flex flex-1 flex-col"
      >
        {notListed ? (
          <NotListedForm />
        ) : selected === null ? (
          <Catalog
            category={category}
            specialty={specialty}
            q={q}
            browseAll={browseAll}
            onFacet={selectFacet}
            onQ={setQ}
            onSelect={setSelected}
            onBrowseAll={() => setBrowseAll(true)}
            onNotListed={() => setNotListed(true)}
            onEmergency={() => navigate('/emergency')}
          />
        ) : (
          <Facilities
            slug={selected}
            onRequest={(hospitalSlug) =>
              navigate(`/service/${selected}/${hospitalSlug}`)
            }
          />
        )}
      </PageBand>
    </PageShell>
  )
}

/**
 * One filter over two taxonomies (ADR-0052): a lab or scan is reachable by its
 * CATEGORY, a clinical procedure by the SPECIALTY that performs it.
 *
 * Derived from the catalogue rather than hard-coded, and that is the fix this
 * ticket exists for. The row used to list `DX_CATEGORIES` — the 15 closed
 * categories — which after M26 reached 34 procedures out of 350 and quietly
 * implied the catalogue was those 15 things. A count sits on every chip so a
 * facet that reaches nothing cannot pretend otherwise.
 */
type Facet = {
  key: string
  name: string
  kind: 'category' | 'specialty'
  count: number
}

function facetsFrom(all: readonly ProcedureListItem[]): Facet[] {
  const categories = new Map<string, Facet>()
  const specialties = new Map<string, Facet>()

  for (const p of all) {
    if (p.category_slug && p.category) {
      const seen = categories.get(p.category_slug)
      if (seen) seen.count += 1
      else
        categories.set(p.category_slug, {
          key: p.category_slug,
          name: p.category,
          kind: 'category',
          count: 1,
        })
    }
    // A procedure counts under every specialty that performs it. ECG is
    // Cardiology AND Internal Medicine AND Emergency Medicine, and all three
    // are honest routes to it.
    for (const s of p.specialties) {
      const seen = specialties.get(s.slug)
      if (seen) seen.count += 1
      else
        specialties.set(s.slug, {
          key: s.slug,
          name: s.name,
          kind: 'specialty',
          count: 1,
        })
    }
  }

  const byName = (a: Facet, b: Facet) => a.name.localeCompare(b.name)
  return [
    ...[...categories.values()].sort(byName),
    ...[...specialties.values()].sort(byName),
  ]
}

/** The unfiltered list, grouped under the heading each procedure sits below. */
function groupByHeading(
  list: readonly ProcedureListItem[],
): Array<[string, ProcedureListItem[]]> {
  const groups = new Map<string, ProcedureListItem[]>()
  for (const p of list) {
    const heading = procedureHeading(p)
    const bucket = groups.get(heading)
    if (bucket) bucket.push(p)
    else groups.set(heading, [p])
  }
  return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]))
}

function Catalog({
  category,
  specialty,
  q,
  browseAll,
  onFacet,
  onQ,
  onSelect,
  onBrowseAll,
  onNotListed,
  onEmergency,
}: {
  category: string | null
  specialty: string | null
  q: string
  browseAll: boolean
  onFacet: (facet: Facet | null) => void
  onQ: (q: string) => void
  onSelect: (slug: string) => void
  onBrowseAll: () => void
  onNotListed: () => void
  onEmergency: () => void
}): ReactNode {
  const navigate = useNavigate()
  // Popular-first by default; searching, picking a facet, or "Search all"
  // switches to the full catalogue browse.
  const filtered = category !== null || specialty !== null
  const browsing = browseAll || q.trim() !== '' || filtered

  // The facet row is built from the WHOLE catalogue so it does not shrink as
  // you type. When nothing is filtered this key is identical to the list query
  // below, so React Query serves both from one request.
  const catalogue = useQuery({
    queryKey: proceduresKey(),
    queryFn: ({ signal }) => fetchProcedures({}, signal),
  })
  const facets = facetsFrom(catalogue.data?.data ?? [])

  const query = useQuery({
    queryKey: proceduresKey({ category, specialty, q }),
    queryFn: ({ signal }) => fetchProcedures({ category, specialty, q }, signal),
  })
  const all = query.data?.data ?? []
  const interpretation = query.data?.interpretation ?? null
  const isEmergency = interpretation?.route === 'emergency'
  // ONE ANSWER, NEVER TWO (M33-T11, ADR-0062). Where the reading panel hands
  // the patient to AMTC, the page adds no second route to the same place (the
  // not-listed door) and no advice to rephrase a query the panel just answered
  // -- the same gate `/specialists` has had since M33-T03.
  const destination =
    interpretation === null ? null : searchDestination(interpretation)
  const handedOver = destination === 'concierge' || destination === 'emergency'
  const list = browsing ? all : all.filter((p) => p.popular)

  // Group only where grouping earns its keep: the unfiltered browse, which is
  // 350 rows. A filtered or searched result is already one coherent set.
  const groups =
    browsing && !filtered && q.trim() === '' ? groupByHeading(list) : null

  const activeKey = specialty ?? category

  return (
    <>
      <div className="px-4 pt-3">
        <SearchInput
          value={q}
          onChange={onQ}
          label="Search tests and procedures"
          placeholder="MRI, knee replacement, blood test…"
        />
      </div>

      {browsing && facets.length > 0 && (
        <div
          role="group"
          aria-label="Filter by category or specialty"
          className="flex gap-1.5 overflow-x-auto px-4 py-3"
        >
          <Chip
            label="All"
            active={activeKey === null}
            onClick={() => onFacet(null)}
          />
          {facets.map((f) => (
            <Chip
              key={`${f.kind}:${f.key}`}
              label={`${f.name} (${f.count})`}
              active={activeKey === f.key}
              onClick={() => onFacet(f)}
            />
          ))}
        </div>
      )}

      {interpretation !== null && (
        <div className="px-4 pt-3">
          <SearchReading
            interpretation={interpretation}
            subject="procedures"
            onEmergency={onEmergency}
            onConcierge={onNotListed}
            // The generalist, not AMTC, when the procedures below are theirs
            // (M33-T11): one voice for the panel and the list.
            onGeneralist={(slug) =>
              navigate(`/specialists?specialty=${encodeURIComponent(slug)}`)
            }
          />
        </div>
      )}

      <p
        className="px-4 pb-1.5 pt-3 font-mono text-[10px] uppercase tracking-wide text-ink-3"
        aria-live="polite"
      >
        {query.isPending
          ? 'Loading…'
          : query.isError
            ? "Couldn't load procedures"
            : browsing
              ? `${list.length} ${list.length === 1 ? 'procedure' : 'procedures'}`
              : 'Popular'}
      </p>

      <div className="flex flex-1 flex-col gap-2 px-4 pb-4">
        {query.isError ? (
          <ErrorBox onRetry={() => void query.refetch()} />
        ) : isEmergency || (handedOver && list.length === 0) ? null : !query.isPending &&
          list.length === 0 ? (
          <p className="px-5 py-8 text-center text-[13px] text-ink-3">
            No procedures found. Try another name, or tell us what you need
            below.
          </p>
        ) : groups ? (
          groups.map(([heading, items]) => (
            <section key={heading} className="flex flex-col gap-2">
              <h2 className="pt-2 font-mono text-[10px] uppercase tracking-wide text-ink-4">
                {heading}{' '}
                <span className="tabular-nums">({items.length})</span>
              </h2>
              {items.map((p) => (
                <ProcedureRow key={p.id} procedure={p} onSelect={onSelect} />
              ))}
            </section>
          ))
        ) : (
          list.map((p) => (
            <ProcedureRow key={p.id} procedure={p} onSelect={onSelect} />
          ))
        )}

        {!browsing && (
          <LinkRow
            icon="search"
            tone="accent"
            label="Search all procedures"
            onClick={onBrowseAll}
          />
        )}
        {!handedOver && (
          <LinkRow
            icon="chat"
            tone="ink"
            label="My procedure is not listed"
            onClick={onNotListed}
          />
        )}
      </div>
    </>
  )
}

function LinkRow({
  icon,
  tone,
  label,
  onClick,
}: {
  icon: 'search' | 'chat'
  tone: 'accent' | 'ink'
  label: string
  onClick: () => void
}): ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-2.5 rounded-r3 border border-line-1 bg-bg-app p-3 text-left text-[13px] font-medium hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent ${
        tone === 'accent' ? 'text-accent-ink' : 'text-ink-2'
      }`}
    >
      <Icon name={icon} size={15} />
      <span className="flex-1">{label}</span>
      <Icon name="chevr" size={14} className="text-ink-3" />
    </button>
  )
}

function ProcedureRow({
  procedure,
  onSelect,
}: {
  procedure: ProcedureListItem
  onSelect: (slug: string) => void
}): ReactNode {
  return (
    <button
      type="button"
      onClick={() => onSelect(procedure.slug)}
      className="flex w-full items-center gap-2.5 rounded-r3 border border-line-1 bg-bg-app p-3 text-left hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
    >
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-ink-1">
          {procedure.name}
        </div>
        <div className="text-[11px] text-ink-3">
          {procedureHeading(procedure)} ·{' '}
          <span className="tabular-nums">{procedure.offer_count}</span>{' '}
          {procedure.offer_count === 1 ? 'hospital' : 'hospitals'}
        </div>
      </div>
      {procedure.popular && (
        <span className="inline-flex shrink-0 items-center rounded-full bg-bed-good-bg px-2 py-0.5 text-[10px] font-semibold text-bed-good">
          Common
        </span>
      )}
      {procedure.referral_required && <ReferralPill />}
      <Icon name="chevr" size={16} />
    </button>
  )
}

/**
 * "My procedure is not listed" — free text + area, gated by a phone OTP (the
 * M10-T02 pattern), POSTed as an AMTC concierge lead. On success it shows the
 * reference; the concierge follows up (no booking here).
 */
function NotListedForm(): ReactNode {
  const [description, setDescription] = useState('')
  const [area, setArea] = useState('')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [session, setSession] = useState<PatientSession | null>(null)
  const [reference, setReference] = useState<string | null>(null)

  const requestMutation = useMutation({
    mutationFn: () => requestOtp(phone.trim()),
    onSuccess: () => setCodeSent(true),
  })
  const verifyMutation = useMutation({
    mutationFn: () => verifyOtp(phone.trim(), code.trim()),
    onSuccess: (s) => setSession(s),
  })
  const submitMutation = useMutation({
    mutationFn: () => {
      if (session === null) throw new Error('not verified')
      const trimmedArea = area.trim()
      return submitProcedureRequest(session.token, {
        description: description.trim(),
        ...(trimmedArea ? { area: trimmedArea } : {}),
      })
    },
    onSuccess: (res) => setReference(res.reference),
  })

  if (reference !== null) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-16 text-center">
        <span className="grid h-12 w-12 place-items-center rounded-full bg-bed-good-bg text-bed-good">
          <Icon name="check" size={22} />
        </span>
        <h1 className="font-display text-d-xs font-medium tracking-display text-ink-1">
          Request sent
        </h1>
        <p className="text-[13px] text-ink-3">
          The AMTC concierge will follow up on your request. Your reference is{' '}
          <span className="font-semibold text-ink-1">{reference}</span>.
        </p>
      </div>
    )
  }

  const ready = description.trim().length >= 3 && session !== null

  return (
    <div className="flex flex-1 flex-col gap-5 px-4 py-5">
      <div>
        <h1 className="text-base font-bold text-ink-1">
          Tell us what you need
        </h1>
        <p className="mt-0.5 text-[13px] text-ink-3">
          Can&apos;t find your test? Describe it and the AMTC concierge will
          find a partner that offers it.
        </p>
      </div>

      <label className="flex flex-col gap-1.5">
        <span className="text-[13px] font-medium text-ink-1">
          What do you need?
        </span>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          aria-label="What do you need?"
          placeholder="e.g. Genetic test for sickle cell, PET-CT with contrast…"
          className="rounded-r3 border border-line-2 bg-bg-app px-3 py-2 text-sm text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        />
      </label>

      <label className="flex flex-col gap-1.5">
        <span className="text-[13px] font-medium text-ink-1">
          Your area <span className="font-normal text-ink-3">(optional)</span>
        </span>
        <input
          type="text"
          value={area}
          onChange={(e) => setArea(e.target.value)}
          aria-label="Your area"
          placeholder="e.g. East Legon, Accra"
          className="rounded-r3 border border-line-2 bg-bg-app px-3 py-2 text-sm text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        />
      </label>

      <div className="flex flex-col gap-2">
        <span className="text-[13px] font-medium text-ink-1">
          Verify your phone
        </span>
        {session ? (
          <p className="inline-flex w-fit items-center gap-1 rounded-full bg-bed-good-bg px-2.5 py-1 text-[13px] font-medium text-bed-good">
            <Icon name="check" size={14} /> Verified · {phone.trim()}
          </p>
        ) : (
          <>
            <div className="flex gap-2">
              <input
                type="tel"
                aria-label="Phone number"
                placeholder="Phone, e.g. +233…"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="flex-1 rounded-r3 border border-line-2 bg-bg-app px-3 py-2 text-sm text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
              />
              <button
                type="button"
                disabled={phone.trim().length < 8 || requestMutation.isPending}
                onClick={() => requestMutation.mutate()}
                className="shrink-0 rounded-r3 border border-accent bg-accent-bg px-3 text-[13px] font-medium text-accent-ink disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
              >
                {requestMutation.isPending
                  ? 'Sending…'
                  : codeSent
                    ? 'Resend'
                    : 'Send code'}
              </button>
            </div>
            {requestMutation.isError && (
              <p role="alert" className="text-[12px] text-bed-bad">
                {errMsg(requestMutation.error)}
              </p>
            )}
            {codeSent && (
              <div className="flex gap-2">
                <input
                  type="text"
                  inputMode="numeric"
                  aria-label="Verification code"
                  placeholder="6-digit code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="flex-1 rounded-r3 border border-line-2 bg-bg-app px-3 py-2 text-sm text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                />
                <button
                  type="button"
                  disabled={code.trim().length < 4 || verifyMutation.isPending}
                  onClick={() => verifyMutation.mutate()}
                  className="shrink-0 rounded-r3 bg-accent px-4 text-[13px] font-medium text-accent-on disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                >
                  Verify
                </button>
              </div>
            )}
            {verifyMutation.isError && (
              <p role="alert" className="text-[12px] text-bed-bad">
                {errMsg(verifyMutation.error)}
              </p>
            )}
          </>
        )}
      </div>

      <button
        type="button"
        disabled={!ready || submitMutation.isPending}
        onClick={() => submitMutation.mutate()}
        className="mt-1 rounded-r3 bg-accent px-4 py-2.5 text-sm font-medium text-accent-on disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        {submitMutation.isPending ? 'Sending…' : 'Send request'}
      </button>
      {submitMutation.isError && (
        <p role="alert" className="text-[12px] text-bed-bad">
          {errMsg(submitMutation.error)}
        </p>
      )}
    </div>
  )
}

function errMsg(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'rate_limited')
      return 'Too many requests. Please try again later.'
    return err.message
  }
  return 'Something went wrong. Please try again.'
}

function Facilities({
  slug,
  onRequest,
}: {
  slug: string
  onRequest: (hospitalSlug: string) => void
}): ReactNode {
  const query = useQuery({
    queryKey: procedureKey(slug),
    queryFn: ({ signal }) => fetchProcedure(slug, signal),
  })

  if (query.isPending) {
    return (
      <p className="px-4 py-12 text-center text-[13px] text-ink-3">Loading…</p>
    )
  }
  if (query.isError) {
    return (
      <div className="px-4 py-12">
        <ErrorBox onRetry={() => void query.refetch()} />
      </div>
    )
  }
  const proc: ProcedureDetail = query.data

  // The reason only exists when some hospital has no figure — and it is the
  // procedure's `price_varies` that decides which reason, not the facility's,
  // because whether a cost CAN be fixed is a property of the procedure.
  const anyUnpriced = proc.facilities.some((f) => f.price_pesewas == null)
  const unpricedReason = anyUnpriced
    ? readPrice(null, { varies: proc.price_varies }).caveat
    : null

  return (
    <div className="flex flex-1 flex-col px-4 pb-4 pt-3">
      <div>
        <h1 className="text-lg font-bold text-ink-1">{proc.name}</h1>
        <p className="mt-0.5 text-xs text-ink-3">
          {procedureHeading(proc)} · offered at{' '}
          <span className="tabular-nums">{proc.facilities.length}</span> of 10
          hospitals
        </p>
        {proc.referral_required && (
          <p className="mt-1.5 inline-flex items-center gap-1 rounded-full bg-accent-bg px-2 py-0.5 text-[11px] font-medium text-accent-ink">
            <Icon name="info" size={11} /> Referral needed
          </p>
        )}
        {proc.prep_notes && (
          <p className="mt-1.5 text-[12px] text-ink-2">
            Prep: {proc.prep_notes}
          </p>
        )}
        {/* Said once, up here, rather than left for the patient to infer from a
            column of identical cells. 91 of the 350 procedures have no single
            price even in principle — charged per session, or part of an
            admission — and that is a different fact from a price we have not
            collected yet (ADR-0052). */}
        {/* THE ACTION IS SHARED; THE REASON IS NOT (ADR-0054 §3, keeping
            ADR-0052's rule alive). Every unpriced row reads the same line —
            AMTC will confirm a price — because the patient's next step is the
            same either way. But "there is no single price for this" and "the
            hospital has not sent it to us" are different facts, and this is
            where the difference is said. Without it the distinction would be
            computed and thrown away. */}
        {unpricedReason !== null && (
          <p className="mt-1.5 text-[12px] text-ink-2">{unpricedReason}</p>
        )}
      </div>

      <div className="mt-3 flex flex-col gap-2">
        {proc.facilities.length === 0 ? (
          <p className="px-5 py-10 text-center text-[13px] text-ink-3">
            No partner hospital currently offers this.
          </p>
        ) : (
          proc.facilities.map((f) => (
            <button
              key={f.hospital_slug}
              type="button"
              onClick={() => onRequest(f.hospital_slug)}
              className="flex w-full items-center justify-between gap-3 rounded-r3 border border-line-1 bg-bg-app p-3 text-left hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-ink-1">
                  {f.hospital_name}
                </div>
                <div className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-ink-3">
                  <Icon name="pin" size={11} /> {f.city}
                  {f.distance_km != null && (
                    <span className="tabular-nums"> · {f.distance_km} km</span>
                  )}
                  {f.turnaround && <span> · {f.turnaround}</span>}
                </div>
              </div>
              <span className="inline-flex shrink-0 items-center gap-1.5">
                <PriceCell
                  pricePesewas={f.price_pesewas}
                  priceVaries={proc.price_varies}
                  priceIsMinimum={f.price_is_minimum}
                />
                <Icon name="chevr" size={16} />
              </span>
            </button>
          ))
        )}
      </div>

      {/* Said once, below the list. A sentence repeated under ten hospital
          prices is a sentence nobody reads — and the owner asked for a
          "general statement", which is exactly what this is (ADR-0054). */}
      {proc.facilities.length > 0 && <PriceCaveat className="mt-3" />}
    </div>
  )
}

/**
 * A price, or an honest account of why there is not one.
 *
 * The two non-amount states are deliberately NOT styled like a figure — no
 * tabular numerals, no bold, quieter ink — because "Not yet available" is a
 * sentence, not a number, and a patient scanning a column of prices should see
 * at a glance which rows carry one.
 */
function PriceCell({
  pricePesewas,
  priceVaries,
  priceIsMinimum,
}: {
  pricePesewas: number | null
  priceVaries: boolean
  priceIsMinimum: boolean
}): ReactNode {
  const reading = readPrice(pricePesewas, {
    varies: priceVaries,
    isMinimum: priceIsMinimum,
  })

  // A figure and a floor are both numbers and look like numbers. "From" is not
  // decoration: it is the difference between what you will pay and the least
  // you could pay, so it is never dropped to save space.
  if (reading.kind === 'amount' || reading.kind === 'minimum') {
    return (
      <span className="tabular-nums text-sm font-semibold text-ink-1">
        {reading.text}
      </span>
    )
  }

  // Not a number, so not dressed as one — no tabular numerals, no bold. A
  // patient scanning the column sees at a glance which rows carry a figure.
  return (
    <span className="max-w-[7.5rem] text-right text-[11px] leading-tight text-ink-3">
      {reading.text}
    </span>
  )
}

function Chip({
  label,
  active,
  onClick,
}: {
  label: string
  active: boolean
  onClick: () => void
}): ReactNode {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={
        active
          ? 'h-8 shrink-0 whitespace-nowrap rounded-full border border-accent bg-accent px-3 text-[13px] font-medium text-accent-on focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'
          : 'h-8 shrink-0 whitespace-nowrap rounded-full border border-line-2 bg-bg-app px-3 text-[13px] font-medium text-ink-1 hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'
      }
    >
      {label}
    </button>
  )
}

function ReferralPill(): ReactNode {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-accent-bg px-1.5 py-0.5 text-[10px] font-semibold text-accent-ink">
      <Icon name="info" size={10} /> Referral
    </span>
  )
}

function ErrorBox({ onRetry }: { onRetry: () => void }): ReactNode {
  return (
    <div
      role="alert"
      className="flex flex-col items-center gap-1 px-5 py-12 text-center"
    >
      <p className="text-[13px] text-ink-3">Couldn&apos;t load. Try again.</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-1 rounded-full bg-accent px-4 py-1.5 text-[13px] font-medium text-accent-on hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
      >
        Retry
      </button>
    </div>
  )
}
